import React from 'react'
import {
  Box,
  Divider,
  Stack,
  TextField,
  Typography,
  Button,
  Paper,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { ThemeApi } from '../../api/themeApi'
import TextEditor from '../Helpers/TextEditor'
import useTranslation from '../../hooks/useTranslation'
import toast from '../../utils/toast'

const getAssetName = (value = '') => {
  if (!value || typeof value !== 'string') return ''
  const cleanValue = value.split('?')[0]
  const parts = cleanValue.split('/')
  return parts[parts.length - 1] || cleanValue
}

function AssetUploadCard({
  title,
  value,
  accept,
  uploadLabel,
  removeLabel,
  currentFileLabel,
  noFileUploadedLabel,
  onUpload,
  onRemove,
}) {
  const fileName = getAssetName(value)

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Stack spacing={2}>
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>

        <Stack direction="row" spacing={1}>
          <Button
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<CloudUploadIcon />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {uploadLabel}
            <input
              type="file"
              accept={accept}
              style={{ display: 'none' }}
              onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
            />
          </Button>

          <Button
            variant="outlined"
            color="error"
            onClick={onRemove}
            disabled={!value}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {removeLabel}
          </Button>
        </Stack>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {currentFileLabel}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {fileName || noFileUploadedLabel}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}

export default function ThemeSettingsPanel({
  themeName,
  settings,
  themeId,
  onThemeNameChange,
  onSettingsChange,
}) {
  const { t } = useTranslation()

  const currentSettings = settings || {}
  const colors = currentSettings.colors || {}
  const footer = currentSettings.footer || {}
  const fonts = currentSettings.fonts || {}

  const updateGroup = (group, key, value) => {
    onSettingsChange({
      ...currentSettings,
      [group]: {
        ...(currentSettings[group] || {}),
        [key]: value,
      },
    })
  }

  const handleAssetUpload = async (assetType, file) => {
    if (!file || !themeId) return

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('assetType', assetType)

      const res = await ThemeApi.uploadThemeAsset(themeId, formData)
      const uploadedUrl = res?.data?.url || ''

      if (assetType === 'normal_fonts') {
        updateGroup('fonts', 'normal', uploadedUrl)
      }

      if (assetType === 'bold_fonts') {
        updateGroup('fonts', 'bold', uploadedUrl)
      }
    } catch (error) {
      console.error('Failed to upload theme asset:', error)
      const isFont = assetType === 'normal_fonts' || assetType === 'bold_fonts'
      toast.error(t(isFont ? 'labels.themes.fontUploadFailed' : 'labels.themes.imageUploadFailed'))
    }
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('labels.themes.theme')}
        </Typography>
        <TextField
          label={t('labels.themes.themeName')}
          fullWidth
          size="small"
          value={themeName || ''}
          onChange={(e) => onThemeNameChange(e.target.value)}
        />
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          {t('labels.themes.colors')}
        </Typography>

        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('labels.themes.primary')}
              type="color"
              size="small"
              fullWidth
              value={colors.primary || '#000000'}
              onChange={(e) => updateGroup('colors', 'primary', e.target.value)}
            />
            <TextField
              label={t('labels.themes.secondary')}
              type="color"
              size="small"
              fullWidth
              value={colors.secondary || '#ffffff'}
              onChange={(e) => updateGroup('colors', 'secondary', e.target.value)}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('labels.themes.text')}
              type="color"
              size="small"
              fullWidth
              value={colors.text || '#000000'}
              onChange={(e) => updateGroup('colors', 'text', e.target.value)}
            />
            <TextField
              label={t('labels.themes.footerBackground')}
              type="color"
              size="small"
              fullWidth
              value={colors.footerBg || '#000000'}
              onChange={(e) => updateGroup('colors', 'footerBg', e.target.value)}
            />
          </Stack>

          <TextField
            label={t('labels.themes.footerText')}
            type="color"
            size="small"
            fullWidth
            value={colors.footerText || '#ffffff'}
            onChange={(e) => updateGroup('colors', 'footerText', e.target.value)}
          />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          {t('labels.themes.footer')}
        </Typography>

        <TextEditor
          value={footer.text || ''}
          onChange={(value) => updateGroup('footer', 'text', value)}
        />
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          {t('labels.themes.fonts')}
        </Typography>

        <Stack spacing={2}>
          <AssetUploadCard
            title={t('labels.themes.normalFont')}
            value={fonts.normal || ''}
            accept=".ttf,.otf,.woff,.woff2"
            removeLabel={t('labels.themes.remove')}
            currentFileLabel={t('labels.themes.currentFile')}
            noFileUploadedLabel={t('labels.themes.noFileUploaded')}
            onUpload={(file) => handleAssetUpload('normal_fonts', file)}
            onRemove={() => updateGroup('fonts', 'normal', '')}
          />

          <AssetUploadCard
            title={t('labels.themes.boldFont')}
            value={fonts.bold || ''}
            accept=".ttf,.otf,.woff,.woff2"
            removeLabel={t('labels.themes.remove')}
            currentFileLabel={t('labels.themes.currentFile')}
            noFileUploadedLabel={t('labels.themes.noFileUploaded')}
            onUpload={(file) => handleAssetUpload('bold_fonts', file)}
            onRemove={() => updateGroup('fonts', 'bold', '')}
          />
        </Stack>
      </Box>
    </Stack>
  )
}